import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import "../../../assets/css/gamesCss/HashTable/searchingChaining.css"
import { Col } from "reactstrap";
import { useLanguage } from "context/LanguageProvider";

const generateQuizQuestions = (targetKey, hashMod, hashTable) => {
    const correctBucket = targetKey % hashMod;
    const bucketChain = hashTable[correctBucket];
    const targetPosition = bucketChain.indexOf(targetKey);

    const questions = [
        {
            question: `Ποια είναι η χειρότερη πολυπλοκότητα αναζήτησης σε Hash Table με chaining;`,
            options: ["O(1)", "O(log n)", "O(n)", "O(n²)"],
            correct: 2,
            explanation: "Στη χειρότερη περίπτωση, όλα τα κλειδιά βρίσκονται στο ίδιο bucket και πρέπει να ψάξουμε όλη τη λίστα: O(n)"
        },
        {
            question: `Το κλειδί ${targetKey} βρίσκεται στο bucket ${correctBucket}. Πόσες συγκρίσεις χρειάζονται για να το βρούμε;`,
            options: [`${targetPosition}`, `${targetPosition + 1}`, `${bucketChain.length}`, `${correctBucket}`],
            correct: 1,
            explanation: `Πρέπει να ελέγξουμε ${targetPosition + 1} κόμβους (συμπεριλαμβανομένου του τελευταίου που είναι το target)`
        },
        {
            question: `Αν το bucket ${correctBucket} έχει ${bucketChain.length} στοιχεία, ποιος είναι ο load factor για αυτό το bucket;`,
            options: [`${bucketChain.length}/${hashMod}`, `${bucketChain.length}`, `${hashMod}/${bucketChain.length}`, `1/${bucketChain.length}`],
            correct: 1,
            explanation: `Ο load factor για ένα bucket είναι απλά ο αριθμός των στοιχείων σε αυτό το bucket: ${bucketChain.length}`
        },
        {
            question: `Με h(k) = k mod ${hashMod}, ποιο άλλο κλειδί θα πήγαινε στο ίδιο bucket με το ${targetKey};`,
            options: [
                `${targetKey + hashMod}`,
                `${targetKey * hashMod}`,
                `${targetKey - 1}`,
                `${targetKey / hashMod}`
            ],
            correct: 0,
            explanation: `Κλειδιά της μορφής k + n×${hashMod} έχουν το ίδιο hash value. Άρα ${targetKey + hashMod} mod ${hashMod} = ${correctBucket}`
        },
        {
            question: `Ποιο είναι το πλεονέκτημα του chaining έναντι του open addressing;`,
            options: [
                "Είναι πιο γρήγορο",
                "Χρησιμοποιεί λιγότερη μνήμη",
                "Δεν έχει clustering",
                "Είναι πιο απλό"
            ],
            correct: 2,
            explanation: "Το chaining αποφεύγει το clustering που εμφανίζεται στο open addressing, καθώς τα collisions διαχειρίζονται με ξεχωριστές λίστες"
        },
        {
            question: `Πόσα buckets χρησιμοποιεί αυτό το Hash Table;`,
            options: [`${hashMod - 1}`, `${hashMod}`, `${hashMod + 1}`, `${hashTable.length}`],
            correct: 1,
            explanation: `Το Hash Table έχει ακριβώς ${hashMod} buckets (0 έως ${hashMod - 1})`
        },
        {
            question: `Αν προσθέσουμε το κλειδί ${targetKey + hashMod * 2}, σε ποιο bucket θα πάει;`,
            options: [
                `${(targetKey + hashMod * 2) % hashMod}`,
                `${correctBucket + 2}`,
                `${targetKey}`,
                "Σε νέο bucket"
            ],
            correct: 0,
            explanation: `${targetKey + hashMod * 2} mod ${hashMod} = ${(targetKey + hashMod * 2) % hashMod}, το ίδιο bucket με το ${targetKey}`
        },
        {
            question: `Ποια είναι η μέση πολυπλοκότητα αναζήτησης με καλή hash function;`,
            options: ["O(1 + α)", "O(n)", "O(log n)", "O(α)"],
            correct: 0,
            explanation: "Η μέση πολυπλοκότητα είναι O(1 + α), όπου α = n/m είναι ο load factor"
        }
    ];

    // Επιλογή 3 τυχαίων ερωτήσεων
    const shuffled = questions.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
};

// Quiz Modal
const QuizModal = ({ isVisible, question, options, onAnswer, correctAnswer, explanation }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [showResult, setShowResult] = useState(false);

    if (!isVisible) return null;

    const handleSelect = (index) => {
        if (showResult) return;
        setSelectedOption(index);
        setShowResult(true);
        setTimeout(() => {
            onAnswer(index === correctAnswer);
            setSelectedOption(null);
            setShowResult(false);
        }, 2500);
    };

    return (
        <div style={{
            display: "flex",
            justifyItems: "center",
            alignContent: "center",
            background: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
        }}>
            <div style={{
                background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                padding: "40px",
                borderRadius: "20px",
                maxWidth: "600px",
                width: "100%",
                border: "3px solid #3b82f6"
            }}>
                <h3 style={{ color: "#3b82f6", fontSize: "1.5rem", marginBottom: "25px", textAlign: "center" }}>
                    ❓ Ερώτηση
                </h3>
                <p style={{ color: "white", fontSize: "1.1rem", marginBottom: "30px", textAlign: "center", lineHeight: "1.6" }}>
                    {question}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    {options.map((option, index) => (
                        <button
                            key={index}
                            onClick={() => handleSelect(index)}
                            disabled={showResult}
                            style={{
                                background: showResult
                                    ? (index === correctAnswer ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                        : index === selectedOption ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                            : "linear-gradient(135deg, #475569 0%, #334155 100%)")
                                    : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "12px",
                                padding: "15px 20px",
                                fontSize: "1rem",
                                fontWeight: "600",
                                cursor: showResult ? "default" : "pointer",
                                transition: "all 0.3s ease",
                                transform: "scale(1)",
                                textAlign: "left"
                            }}
                            onMouseEnter={(e) => !showResult && (e.target.style.transform = "scale(1.02)")}
                            onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                        >
                            {option}
                            {showResult && index === correctAnswer && " ✓"}
                            {showResult && index === selectedOption && index !== correctAnswer && " ✗"}
                        </button>
                    ))}
                </div>
                {showResult && (
                    <div style={{
                        marginTop: "25px",
                        padding: "15px",
                        borderRadius: "10px",
                        background: selectedOption === correctAnswer
                            ? "rgba(16, 185, 129, 0.2)"
                            : "rgba(239, 68, 68, 0.2)",
                        border: `2px solid ${selectedOption === correctAnswer ? "#10b981" : "#ef4444"}`,
                        color: "white",
                        fontSize: "0.95rem",
                        lineHeight: "1.6"
                    }}>
                        <strong>{selectedOption === correctAnswer ? "✓ Σωστό!" : "✗ Λάθος!"}</strong>
                        <br />
                        {explanation}
                    </div>
                )}
            </div>
        </div>
    );
};


const createHashTable = (size, keys, hashMod) => {
    const table = Array(size).fill(null).map(() => []);
    keys.forEach(key => {
        const index = key % hashMod;
        table[index].push(key);
    });
    return table;
};

const HashTableSearchingChaining = () => {
    const [hashMod, setHashMod] = useState(7);
    const [tableSize, setTableSize] = useState(7);
    const [allKeys, setAllKeys] = useState([]);
    const [hashTable, setHashTable] = useState([]);
    const [targetKey, setTargetKey] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [userHashInput, setUserHashInput] = useState("");
    const [selectedBucket, setSelectedBucket] = useState(null);
    const [visitedChainIndices, setVisitedChainIndices] = useState([]);
    const [lives, setLives] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [message, setMessage] = useState("");
    const [isCorrectBucket, setIsCorrectBucket] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(9);
    const [timerActive, setTimerActive] = useState(false);
    const [successfulRounds, setSuccessfulRounds] = useState(0);
    const [difficultyLevel, setDifficultyLevel] = useState(1);
    const [gameStarted, setGameStarted] = useState(false);
    const { selLanguage, a, b } = useLanguage();

    // Quiz states
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);

    useEffect(() => {
        prepareGame();
    }, []);

    useEffect(() => {
        if (lives <= 0 && gameStarted) {
            setShowGameOver(true);
            setTimerActive(false);
        }
    }, [lives, gameStarted]);

    // Timer effect
    useEffect(() => {
        let interval;
        if (timerActive && timeRemaining > 0) {
            interval = setInterval(() => {
                setTimeRemaining(prev => prev - 1);
            }, 1000);
        } else if (timeRemaining === 0 && timerActive) {
            setTimerActive(false);
            setMessage(selLanguage ? " Τέλειωσε ο χρόνος! Χάνεις μία ζωή." : "Time is up. You lose 1 life.");
            setLives(prev => prev - 1);
            setTimeout(() => {
                setMessage("");
                selectNewTarget();
            }, 2000);
        }
        return () => clearInterval(interval);
    }, [timerActive, timeRemaining]);

    const generateComplexHashTable = (level) => {
        let randomMod;
        let numberOfKeys;

        if (level === 1) {
            randomMod = Math.floor(Math.random() * 3) + 5;
            numberOfKeys = Math.floor(Math.random() * 5) + 12;
        } else if (level === 2) {
            randomMod = Math.floor(Math.random() * 3) + 7;
            numberOfKeys = Math.floor(Math.random() * 6) + 18;
        } else {
            randomMod = Math.floor(Math.random() * 4) + 9;
            numberOfKeys = Math.floor(Math.random() * 11) + 25;
        }

        setHashMod(randomMod);
        setTableSize(randomMod);

        const keys = new Set();
        while (keys.size < numberOfKeys) {
            keys.add(Math.floor(Math.random() * 100) + 1);
        }
        const keysArray = Array.from(keys);
        setAllKeys(keysArray);

        const table = createHashTable(randomMod, keysArray, randomMod);
        setHashTable(table);

        let targetBucket;
        let targetIndex;
        let attempts = 0;

        do {
            const randomKey = keysArray[Math.floor(Math.random() * keysArray.length)];
            const bucketIdx = randomKey % randomMod;
            targetBucket = table[bucketIdx];
            targetIndex = targetBucket.indexOf(randomKey);
            attempts++;

            if (attempts > 50) {
                return { table, keysArray, randomMod, targetKey: randomKey };
            }
        } while (targetBucket.length < 2 || targetIndex === 0);

        return { table, keysArray, randomMod, targetKey: targetBucket[targetIndex] };
    };

    const prepareGame = () => {
        const { table, keysArray, randomMod, targetKey: firstTarget } = generateComplexHashTable(1);
        setTargetKey(firstTarget);
        setSuccessfulRounds(0);
        setDifficultyLevel(1);
        resetRoundState(false);
    };

    const startGame = () => {
        setGameStarted(true);
        setTimerActive(true);
    };

    const resetRoundState = (startTimer = true) => {
        setCurrentStep(0);
        setUserHashInput("");
        setSelectedBucket(null);
        setVisitedChainIndices([]);
        setMessage("");
        setIsCorrectBucket(false);
        setTimeRemaining(9);
        setShowQuiz(false);
        setCurrentQuizIndex(0);
        if (startTimer && gameStarted) {
            setTimerActive(true);
        }
    };

    const resetGame = () => {
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);
        setGameStarted(false);
        setTimerActive(false);
        prepareGame();
    };

    const selectNewTarget = () => {
        let targetBucket;
        let targetIndex;
        let attempts = 0;
        let newTarget;

        do {
            newTarget = allKeys[Math.floor(Math.random() * allKeys.length)];
            const bucketIdx = newTarget % hashMod;
            targetBucket = hashTable[bucketIdx];
            targetIndex = targetBucket.indexOf(newTarget);
            attempts++;

            if (attempts > 50) {
                break;
            }
        } while (targetBucket.length < 2 || targetIndex === 0);

        setTargetKey(newTarget);
        resetRoundState();
    };

    const increaseComplexity = () => {
        const newSuccessCount = successfulRounds + 1;
        setSuccessfulRounds(newSuccessCount);

        if (newSuccessCount >= 3) {
            setShowGameWon(true);
            setTimerActive(false);
            return;
        }

        const newLevel = newSuccessCount + 1;
        setDifficultyLevel(newLevel);

        const { table, keysArray, randomMod, targetKey: newTarget } = generateComplexHashTable(newLevel);
        setTargetKey(newTarget);

        setMessage(selLanguage ? ` Σωστά! Επίπεδο ${newLevel}/3.` : ` Correct! Level ${newLevel}/3.`);

        setTimeout(() => {
            setMessage("");
            resetRoundState();
        }, 2000);
    };

    const checkHashInput = () => {
        const correctIndex = targetKey % hashMod;
        const userInput = parseInt(userHashInput);

        if (userInput === correctIndex) {
            setTimerActive(false);
            setCurrentStep(1);
        } else {
            setTimerActive(false);
            setMessage(selLanguage ? "Λάθος. Πρσοπάθησε ξανά." : "Wrong. Try again.");
            setLives(prev => prev - 1);
            setTimeout(() => {
                setMessage("");
                selectNewTarget();
            }, 2000);
        }
    };

    const selectBucket = (index) => {
        if (currentStep !== 1) return;

        const correctIndex = targetKey % hashMod;
        setSelectedBucket(index);

        if (index === correctIndex) {
            setMessage(selLanguage ? " Σωστό bucket! Τώρα βρες το κλειδί ψάχνοντας σειριακά τη λίστα" : "Correct bucket. No search for the key by pressing the elements one by one.");
            setCurrentStep(2);
            setIsCorrectBucket(true);
        } else {
            setMessage(selLanguage ? " Λάθος bucket! Δοκίμασε ξανά" : "Wrong bucket. Try again.");
            setLives(prev => prev - 1);
            setTimeout(() => {
                setMessage("");
                setSelectedBucket(null);
            }, 2000);
        }
    };

    const selectChainNode = (bucketIndex, chainIndex) => {
        if (currentStep !== 2 || !isCorrectBucket || bucketIndex !== selectedBucket) return;

        if (chainIndex !== visitedChainIndices.length) {
            setMessage(selLanguage ? " Πρέπει να ελέγξεις τους κόμβους με τη σειρά από αριστερά προς τα δεξιά!" : "You have to search the nodes one by one from the left to the right.");
            setLives(prev => prev - 1);
            setTimeout(() => {
                setMessage("");
            }, 2000);
            return;
        }

        const selectedKey = hashTable[bucketIndex][chainIndex];
        setVisitedChainIndices([...visitedChainIndices, chainIndex]);

        if (selectedKey === targetKey) {
            setMessage(selLanguage ? " Βρήκες το κλειδί! Τώρα απάντησε στις ερωτήσεις..." : "You found the key! Now answer the following questions.");
            setTimerActive(false);

            // Δημιουργία ερωτήσεων
            const questions = generateQuizQuestions(targetKey, hashMod, hashTable);
            setQuizQuestions(questions);
            setCurrentQuizIndex(0);

            setTimeout(() => {
                setShowQuiz(true);
                setMessage("");
            }, 1500);
        }
        // Δεν εμφανίζουμε μήνυμα για ενδιάμεσους κόμβους - είναι μέρος της σωστής διαδικασίας
    };

    const handleQuizAnswer = (isCorrect) => {
        if (!isCorrect) {
            setLives(prev => prev - 1);
            setShowQuiz(false);
            setTimeout(() => {
                selectNewTarget();
            }, 500);
            return;
        }

        // Μόλις απαντήσει (σωστά ή λάθος), τελειώνει το quiz
        setShowQuiz(false);
        increaseComplexity();

    };

    return (

        <div
            className="main-content searchingHashContainer"

        >
            <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
            <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />



            <Col xl="12" lg="12" md="12" sm="12" xs="12" style={{
                display: "flex",
                alignContent: "center",
                width: "100%",
                justifyContent: "center"

            }}>
                <div
                    style={{
                        display: "flex",
                        alignContent: "center",
                        width: "100%",
                        justifyContent: "center"

                    }}>

                    {showQuiz && quizQuestions[currentQuizIndex] && (
                        <QuizModal
                            isVisible={showQuiz}
                            question={quizQuestions[currentQuizIndex].question}
                            options={quizQuestions[currentQuizIndex].options}
                            correctAnswer={quizQuestions[currentQuizIndex].correct}
                            explanation={quizQuestions[currentQuizIndex].explanation}
                            onAnswer={handleQuizAnswer}
                        />
                    )}

                    <div style={{ width: "100%", maxWidth: "900px" }}>
                        {/* Lives Display */}
                        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "row",
                                justifyContent: "right",
                                gap: "12px"
                            }}>


                                <div style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "center"
                                }}>
                                    {[1, 2, 3].map((heartNum) => (
                                        <div
                                            key={heartNum}
                                            style={{
                                                fontSize: "24px",
                                                filter: lives >= heartNum
                                                    ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                    : "grayscale(100%) opacity(0.3)",
                                                transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                                transition: "all 0.3s ease",
                                                animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                            }}
                                        >
                                            ❤️
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <style>{`
                        @keyframes heartbeat {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                        }
                        @keyframes pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.5; }
                        }
                        .checking-animation {
                            animation: pulse 1s ease-in-out infinite;
                        }
                    `}</style>
                        </div>
                        <br></br>

                        {/* Instructions */}
                        <div className="searchingHashProm">
                            <div>
                                <strong>{selLanguage ? "Βρες το κλειδί" : "Find the key"}: {targetKey}</strong>
                                <br />
                                Hash Function: h(k) = k mod {hashMod}
                                <br />
                                {!gameStarted && selLanguage ? "Στο παιχνίδι αυτό θα πρέπει να εκτελέσεις μαι διαδικασία αναζήτησης μέσα σε περιορισμένο χρόνο! Θα πρέπει να εκτελέσεις την διαδικασία 3 φορές σωστά για να κερδίσεις!" : 'In this game, you need to complete a search task within a time limit! You must complete the task correctly 3 times to win!'}
                                {gameStarted && currentStep === 0 && selLanguage ? "Βήμα 1: Υπολόγισε το hash index" : " Step 1: Calculate the hash index"}
                                {gameStarted && currentStep === 1 && selLanguage ? "Βήμα 2: Επίλεξε το σωστό bucket" : " Step 2: Choose the correct bucket."}
                                {gameStarted && currentStep === 2 && selLanguage ? "Βήμα 3: Βρες το κλειδί ψάχνοντας σειριακά τη λίστα" : " Step 3: Find the key on the list by searchign the nodes one by one."}
                            </div>
                        </div>

                        {/* Start Button */}
                        {!gameStarted && (
                            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                                <button
                                    onClick={startGame}
                                    className="searchingHashStartBtn"
                                    onMouseEnter={(e) => e.target.style.transform = "scale(1.05)"}
                                    onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                                >
                                    {selLanguage ? "Έναρξη Παιχνιδιού" : "Start game"}
                                </button>
                            </div>
                        )}

                        {/* Hash Calculation Input */}
                        {gameStarted && currentStep === 0 && (
                            <div>
                                {/* Timer Display */}
                                <div style={{
                                    textAlign: "center",
                                    marginBottom: "15px"
                                }}>
                                    <div style={{
                                        display: "inline-block",
                                        padding: "10px 20px",
                                        borderRadius: "12px",
                                        background: timeRemaining <= 3
                                            ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                            : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                        color: "white",
                                        fontSize: "1.5rem",
                                        fontWeight: "700",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                                        animation: timeRemaining <= 3 ? "pulse 0.5s infinite" : "none"
                                    }}>
                                        {timeRemaining}s
                                    </div>
                                </div>

                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "10px",
                                    marginBottom: "20px",
                                    flexWrap: "wrap"
                                }}>
                                    <input
                                        type="number"
                                        value={userHashInput}
                                        onChange={(e) => setUserHashInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                checkHashInput();
                                            }
                                        }}
                                        placeholder="index..."
                                        style={{
                                            padding: "10px 20px",
                                            borderRadius: "10px",
                                            border: "2px solid #3b82f6",
                                            fontSize: "1rem",
                                            width: "150px"
                                        }}
                                    />
                                    <button
                                        onClick={checkHashInput}
                                        style={{
                                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "10px",
                                            padding: "10px 20px",
                                            fontSize: "1rem",
                                            fontWeight: "600",
                                            cursor: "pointer"
                                        }}
                                    >
                                        {selLanguage ? "Έλεγχος" : "Check"}
                                    </button>
                                </div>

                                <style>{`
              @keyframes pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.05); }
              }
            `}</style>
                            </div>
                        )}

                        {/* Hash Table Visualization */}
                        {gameStarted && currentStep > 0 && (
                            <div className="searchingHashFrame">
                                <div style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                    padding: "20px",
                                    height: "100%",
                                    overflowY: "auto"
                                }}>
                                    {hashTable.map((bucket, bucketIndex) => (
                                        <div key={bucketIndex} style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            minHeight: "60px"
                                        }}>
                                            {/* Bucket Index */}
                                            <div
                                                onClick={() => selectBucket(bucketIndex)}
                                                className="searchingHashBucketIndex"
                                                style={{
                                                    width: "60px",
                                                    height: "60px",
                                                    borderRadius: "10px",
                                                    background: selectedBucket === bucketIndex
                                                        ? (isCorrectBucket ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)")
                                                        : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                                    color: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: "700",
                                                    fontSize: "1.2rem",
                                                    cursor: currentStep === 1 ? "pointer" : "default",
                                                    border: "3px solid #1f2937",
                                                    flexShrink: 0,
                                                    transition: "all 0.3s ease",
                                                    transform: selectedBucket === bucketIndex ? "scale(1.1)" : "scale(1)"
                                                }}
                                            >
                                                {bucketIndex}
                                            </div>

                                            {/* Arrow */}
                                            <div style={{
                                                color: "#3b82f6",
                                                fontSize: "1.5rem",
                                                fontWeight: "700",
                                                flexShrink: 0
                                            }}>
                                                →
                                            </div>

                                            {/* Chain */}
                                            <div style={{
                                                display: "flex",
                                                gap: "10px",
                                                flexWrap: "wrap",
                                                flex: 1
                                            }}>
                                                {bucket.length === 0 ? (
                                                    <div style={{
                                                        padding: "10px 20px",
                                                        background: "#f1f5f9",
                                                        borderRadius: "8px",
                                                        color: "#64748b",
                                                        fontStyle: "italic"
                                                    }}>
                                                        Άδειο
                                                    </div>
                                                ) : (
                                                    bucket.map((key, chainIndex) => (
                                                        <React.Fragment key={chainIndex}>
                                                            <motion.div
                                                                onClick={() => selectChainNode(bucketIndex, chainIndex)}
                                                                whileHover={{ scale: currentStep === 2 && isCorrectBucket && bucketIndex === selectedBucket ? 1.1 : 1 }}
                                                                style={{
                                                                    padding: "10px 20px",
                                                                    borderRadius: "8px",
                                                                    background: visitedChainIndices.includes(chainIndex) && selectedBucket === bucketIndex
                                                                        ? (key === targetKey ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "#94a3b8")
                                                                        : "#e0e0e0ff",
                                                                    color: "linear-gradient(135deg, rgba(3, 50, 126, 1) 0%,",
                                                                    fontWeight: "700",
                                                                    cursor: currentStep === 2 && isCorrectBucket && bucketIndex === selectedBucket ? "pointer" : "default",
                                                                    border: "2px solid #01347aff",
                                                                    opacity: (visitedChainIndices.includes(chainIndex) && key !== targetKey) ? 0.5 : 1,
                                                                    transition: "all 0.3s ease",
                                                                    position: "relative"
                                                                }}
                                                            >
                                                                {key}
                                                                {visitedChainIndices.includes(chainIndex) && selectedBucket === bucketIndex && (
                                                                    <div style={{
                                                                        position: "absolute",
                                                                        top: "-8px",
                                                                        right: "-8px",
                                                                        background: key === targetKey ? "#10b981" : "#64748b",
                                                                        borderRadius: "50%",
                                                                        width: "20px",
                                                                        height: "20px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: "0.7rem",
                                                                        border: "2px solid white"
                                                                    }}>
                                                                        {key === targetKey ? "✓" : "✗"}
                                                                    </div>
                                                                )}
                                                            </motion.div>
                                                            {chainIndex < bucket.length - 1 && (
                                                                <div style={{
                                                                    color: "#3b82f6",
                                                                    fontSize: "1.2rem",
                                                                    display: "flex",
                                                                    alignItems: "center"
                                                                }}>
                                                                    →
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Message Display */}
                        {message && (
                            <div style={{
                                textAlign: "center",
                                padding: "15px",
                                borderRadius: "16px",
                                marginTop: "20px",
                                background: message.includes("✅") || message.includes("🎉")
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                border: message.includes("✅") || message.includes("🎉") ? "2px solid #10b981" : "2px solid #ef4444",
                                color: message.includes("✅") || message.includes("🎉") ? "#059669" : "#dc2626",
                                fontWeight: "600",
                                fontSize: "1rem"
                            }}>
                                {message}
                            </div>
                        )}

                        {/* Progress */}
                        {gameStarted && (
                            <div style={{
                                textAlign: "center",
                                marginTop: "20px",
                                color: "white",
                                fontSize: "1rem",
                                fontWeight: "600"
                            }}>
                                {selLanguage ? "Επιτυχημένα Rounds" : "Rounds Won"}: {successfulRounds} / 3
                            </div>
                        )}
                    </div>
                </div>
            </Col>
        </div>
    );
};

export default HashTableSearchingChaining;